/*
  ProfileOptionsSettings — clinic-editable option lists for the Client
  Details tab's Gender + Pronouns dropdowns. Stored via the settings
  singleton at ``db.settings._id="profile_options"``; empty lists fall
  back to the built-in defaults so a mis-configured clinic still gets
  sensible dropdowns.

  Placement: Settings → Onboarding & Forms (alongside intake templates).
*/
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Save, Undo2, X, Plus } from "lucide-react";
import { toast } from "sonner";


function OptionListEditor({ title, hint, values, onChange, testid }) {
  const [draft, setDraft] = useState("");
  return (
    <Card className="p-4 border-zinc-200">
      <div className="text-2xs uppercase tracking-wider text-zinc-500 font-medium">
        {title}
      </div>
      {hint && (
        <div className="text-xs text-zinc-500 mt-1">{hint}</div>
      )}
      <ul className="mt-3 space-y-1.5" data-testid={`${testid}-list`}>
        {values.map((v, i) => (
          <li key={`${v}-${i}`} className="flex items-center gap-2 text-sm">
            <span className="flex-1 rounded border border-zinc-200 bg-zinc-50 px-2 py-1"
                   data-testid={`${testid}-row-${i}`}>
              {v}
            </span>
            <Button
              size="sm" variant="ghost"
              className="h-7 px-2 text-zinc-500 hover:text-red-600"
              onClick={() => onChange(values.filter((_, idx) => idx !== i))}
              title="Remove option"
              data-testid={`${testid}-remove-${i}`}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </li>
        ))}
        {values.length === 0 && (
          <li className="text-xs italic text-zinc-500 py-1">
            No custom options — defaults will apply.
          </li>
        )}
      </ul>
      <div className="mt-2 flex gap-2">
        <input
          type="text" value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim()) {
              e.preventDefault();
              onChange([...values, draft.trim()]);
              setDraft("");
            }
          }}
          placeholder="Add option and press Enter"
          className="flex-1 h-8 rounded border border-zinc-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-400"
          data-testid={`${testid}-input`}
        />
        <Button
          size="sm" variant="outline" className="h-8"
          onClick={() => {
            const t = draft.trim();
            if (!t) return;
            onChange([...values, t]);
            setDraft("");
          }}
          disabled={!draft.trim()}
          data-testid={`${testid}-add`}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add
        </Button>
      </div>
    </Card>
  );
}


export default function ProfileOptionsSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gender, setGender] = useState([]);
  const [pronouns, setPronouns] = useState([]);
  const [defaults, setDefaults] = useState({ gender: [], pronouns: [] });

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get("/settings/profile-options");
      setGender(r.data?.gender || []);
      setPronouns(r.data?.pronouns || []);
      setDefaults(r.data?.defaults || { gender: [], pronouns: [] });
    } catch (e) {
      toast.error("Could not load options", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }

  useEffect(() => { refresh(); }, []);

  async function save() {
    setSaving(true);
    try {
      await api.put("/settings/profile-options", { gender, pronouns });
      toast.success("Profile options saved");
      await refresh();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  function resetToDefaults() {
    if (!window.confirm("Reset both lists to the built-in defaults?")) return;
    setGender([...(defaults.gender || [])]);
    setPronouns([...(defaults.pronouns || [])]);
  }

  return (
    <Card
      className="p-6 rounded-lg border border-zinc-200 shadow-none"
      data-testid="profile-options-settings"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Client fields — Gender & Pronouns
          </Label>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Edit the option lists that appear on the Client Details tab.
            Add trans-inclusive, culturally specific, or clinic-specific
            entries. Empty lists fall back to the built-in defaults so
            the dropdowns are never blank.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm" variant="outline" onClick={resetToDefaults}
            data-testid="profile-options-reset"
          >
            <Undo2 className="h-3.5 w-3.5 mr-1" /> Reset defaults
          </Button>
          <Button
            size="sm" onClick={save} disabled={saving || loading}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="profile-options-save"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <OptionListEditor
          title="Gender"
          hint='Common allied-health additions: "Trans woman", "Trans man", "Intersex", "Agender".'
          values={gender}
          onChange={setGender}
          testid="profile-options-gender"
        />
        <OptionListEditor
          title="Pronouns"
          hint='Common additions: "xe/xir", "ze/hir", "any pronouns", "ask me".'
          values={pronouns}
          onChange={setPronouns}
          testid="profile-options-pronouns"
        />
      </div>
    </Card>
  );
}
